// ── Production helpers ──────────────────────────────────────────────────────

import { getComponentSortRank } from '@/types/database';

/**
 * The next assembly order number in the ASO-NNNNN sequence.
 *
 * Derived from the latest existing number rather than a counter, so the caller
 * must pass the genuine maximum - handing it a stale value produces a duplicate.
 *
 * Padding is a minimum, not a limit: past ASO-99999 the numbers simply get
 * longer rather than wrapping or truncating.
 */
export function nextAsoNumber(latest: string | null | undefined): string {
  if (!latest) return 'ASO-00001';
  const match = latest.match(/ASO-(\d+)/i);
  if (!match) return 'ASO-00001';
  const next = parseInt(match[1], 10) + 1;
  return `ASO-${String(next).padStart(5, '0')}`;
}

/**
 * Midnight on the Monday of the current week, as an ISO string.
 *
 * Computed in the *local* timezone and then serialised to UTC, so the result
 * shifts with the machine running it - which is intended, since it filters a
 * view for whoever is looking at it, not a report with a fixed cutoff.
 */
export function startOfWeekISO(now: Date = new Date()): string {
  const d = new Date(now.getTime());
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7)); // getDay(): 0 = Sunday
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

/**
 * How much of one reservation component is still missing, split into the part
 * a purchase order will deliver (awaiting) and the part nobody has ordered yet.
 *
 * Uses the same arithmetic as the Units Pending stat - allocated stock first,
 * then PO cover - so the parts behind a pill always account for its number.
 * `unitsNeeded` is the reservation's unfulfilled units, not its full quantity:
 * posting an order deducts from `allocated` too, so both sides shrink together.
 */
export function splitShortfall(c: {
  unitsNeeded: number;
  perBuild: number;
  allocated: number;
  poCovered: number;
}): { awaiting: number; toOrder: number } {
  const shortfall = Math.max(0, c.unitsNeeded * (c.perBuild || 1) - c.allocated);
  const awaiting = Math.min(shortfall, Math.max(0, c.poCovered));
  return { awaiting, toOrder: shortfall - awaiting };
}

export interface PendingPartLine {
  sku: string;
  description: string;
  componentType: string;
  reservationId: string;
  awaiting: number;
  toOrder: number;
}

export interface PendingPart {
  sku: string;
  description: string;
  componentType: string;
  total: number;
  reservations: { reservationId: string; qty: number }[];
}

/**
 * Collapses per-reservation lines into one row per part for the chosen bucket,
 * busiest reservation first, parts in the canonical build order.
 */
export function groupPendingParts(lines: PendingPartLine[], kind: 'awaiting' | 'toOrder'): PendingPart[] {
  const bySku = new Map<string, PendingPart>();
  for (const line of lines) {
    const qty = line[kind];
    if (qty <= 0) continue;
    let part = bySku.get(line.sku);
    if (!part) {
      part = { sku: line.sku, description: line.description, componentType: line.componentType, total: 0, reservations: [] };
      bySku.set(line.sku, part);
    }
    part.total += qty;
    part.reservations.push({ reservationId: line.reservationId, qty });
  }
  const parts = [...bySku.values()];
  for (const p of parts) p.reservations.sort((a, b) => b.qty - a.qty);
  return parts.sort((a, b) =>
    getComponentSortRank(a.componentType) - getComponentSortRank(b.componentType) || a.sku.localeCompare(b.sku),
  );
}
